import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import words from 'lodash/words';
import deburr from 'lodash/deburr';
import find from 'lodash/find';

import { parseFullName } from '@jerry-serverless/name-parser';

import type { NameVariation } from '../types/name';

/**
 * Provide 3 fixed options for name variation.
 * 1) First word, Rest of words, Last word
 * 2) First two words, Rest of words, Last word
 * 3) First word, Rest of words, Last two words
 */
const nameVariationIndexes = [
  { fnameIndex: 1, lnameIndex: -1 },
  { fnameIndex: 2, lnameIndex: -1 },
  { fnameIndex: 1, lnameIndex: -2 },
];

/**
 * Define supported suffix list.
 */
export const supportedSuffixList = ['Jr', 'Sr', 'I', 'II', 'III'];

/**
 * Parses name by filtering out prefixes or suffixes and
 * concatenating first, middle, last name.
 * eg; Dr Moon Jerry Jerry Jr => Moon Jerry Jerry
 */
export const parseExtractName = (name?: string): { parts: string[]; suffix?: string } => {
  if (!name) return { parts: [] };
  const parsedName = parseFullName(name, 'all');

  const first = words(parsedName.first?.trim()) ?? [];
  const middle = words(parsedName.middle?.trim()) ?? [];
  const last = words(parsedName.last?.trim()) ?? [];

  return { parts: [...first, ...middle, ...last], suffix: parsedName.suffix };
};

/**
 * Generate first name, middle name, last name combinations using 'nameVariationIndexes'
 */
export const getNameVariations = (name: string): NameVariation[] => {
  const splitParts = parseExtractName(name);
  const nameSplit = splitParts.parts;
  const options: NameVariation[] = [];

  const parsedSuffix = splitParts.suffix ? splitParts.suffix.trim() : '';
  const foundMatchSuffix = find(supportedSuffixList, (suffix) => suffix.toLowerCase() === parsedSuffix.toLowerCase());

  if (splitParts.parts.length === 2) {
    options.push({ firstName: nameSplit[0], middleName: '', lastName: nameSplit[1], suffix: foundMatchSuffix });
    return options;
  }

  map(nameVariationIndexes, ({ fnameIndex, lnameIndex }) => {
    // Create variation.
    const firstName = nameSplit.slice(0, fnameIndex).join(' ');
    const middleName = nameSplit.slice(fnameIndex, nameSplit.length + lnameIndex).join(' ');
    const lastName = nameSplit.slice(nameSplit.length + lnameIndex, nameSplit.length).join(' ');
    options.push({ firstName, middleName, lastName, suffix: foundMatchSuffix });
  });
  return options;
};

/**
 * Check if name variation option should be selected.
 */
export const checkNameVariationSelected = (variation: NameVariation, nameVariationData?: NameVariation): boolean =>
  !!nameVariationData && isEqual(nameVariationData, variation);

/**
 * Define label and placeholder value for each name part input.
 */
export const nameVariationLabel = {
  firstName: { label: 'First name', placeholder: 'e.g. Jerry' },
  middleName: { label: 'Middle name', placeholder: 'e.g. Alex' },
  lastName: { label: 'Last name', placeholder: 'e.g. Maguire' },
  suffix: { label: 'Suffix (optional)', placeholder: 'e.g. Jr' },
};

/**
 * Check original name string whether contains accent letters inside.
 */
export const checkNameHasAccent = (name: string): { nameWithoutAccent: string; hasAccent: boolean } => {
  const nameWithoutAccent = deburr(name);
  const hasAccent = nameWithoutAccent !== name;
  return { nameWithoutAccent, hasAccent };
};

/**
 * Check original name string whether contains invalid letters inside.
 * @param name
 */
export function hasInvalidLetter(name: string): boolean {
  // \u00C0-\u017F includes Latin Extended-A
  return name !== name.replace(/[^a-zA-Z0-9\u00C0-\u017F- ]/g, '');
}
